/**
 * Notes on a wine. Shared across users, because the catalogue is shared — one
 * person's "far better on the second night" is worth the next person reading.
 */
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import { api, type Comment } from '../lib/api'

export default function Comments({
  wineId,
  comments,
  me,
  displayName,
  onChanged,
}: {
  wineId: string
  comments: Comment[]
  me: string
  displayName: string
  onChanged: () => void
}) {
  const [text, setText] = useState('')

  const post = useMutation({
    mutationFn: () => api.comment(wineId, text.trim(), displayName),
    onSuccess: () => {
      setText('')
      onChanged()
    },
  })

  const remove = useMutation({
    mutationFn: (commentId: string) => api.deleteComment(wineId, commentId),
    onSuccess: onChanged,
  })

  return (
    <section className="space-y-3">
      <h3 className="label">Notes</h3>

      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (text.trim()) post.mutate()
        }}
      >
        <input
          value={text}
          onChange={(event) => setText(event.target.value)}
          maxLength={2000}
          placeholder="Drank it with lamb…"
          className="min-w-0 flex-1 rounded-full border border-white/10 bg-black/25 px-4 py-2.5 text-sm placeholder:text-stone-600 focus:border-wine-500/60 focus:outline-none"
        />
        <button className="btn-primary shrink-0" disabled={!text.trim() || post.isPending}>
          Post
        </button>
      </form>

      {post.isError && (
        <p className="text-xs text-rose-300">{(post.error as Error).message}</p>
      )}

      <ul className="space-y-2">
        {comments.map((comment) => (
          <li key={comment.commentId} className="card p-3">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-medium">{comment.displayName}</span>
              <span className="text-[11px] text-stone-600">
                {comment.createdAt ? new Date(comment.createdAt).toLocaleDateString() : ''}
              </span>
              {comment.userId === me && (
                <button
                  onClick={() => remove.mutate(comment.commentId)}
                  className="ml-auto text-[11px] text-stone-600 hover:text-rose-300"
                >
                  delete
                </button>
              )}
            </div>
            <p className="mt-1 text-sm whitespace-pre-wrap text-stone-300">{comment.text}</p>
          </li>
        ))}
      </ul>

      {!comments.length && (
        <p className="text-xs text-stone-500">No notes on this one yet.</p>
      )}
    </section>
  )
}
